// youtube-url-converter.js
// Converts ANY YouTube shareable URL to the proper embed format

/**
 * Converts various YouTube URL formats to embed-ready URLs
 * Handles: watch URLs, share URLs, live URLs, shorts, playlists
 */
export function convertToEmbedUrl(url) {
  if (!url) return null

  try {
    // const urlObj = new URL(url)

    // Handle different YouTube URL patterns
    const patterns = [
      // Live stream URLs: /live/videoId
      {
        pattern: /\/live\/([\w-]{8,15})/,
        type: "video",
        handler: (match) => match[1],
      },
      // Standard watch URLs: /watch?v=videoId
      {
        pattern: /\/watch\?.*v=([\w-]{8,15})/,
        type: "video",
        handler: (match) => match[1],
      },
      // Short URLs: youtu.be/videoId
      {
        pattern: /youtu\.be\/([\w-]{8,15})/,
        type: "video",
        handler: (match) => match[1],
      },
      // Shorts URLs: /shorts/videoId
      {
        pattern: /\/shorts\/([\w-]{8,15})/,
        type: "video",
        handler: (match) => match[1],
      },
      // Embed URLs (already correct format): /embed/videoId
      {
        pattern: /\/embed\/([\w-]{8,15})/,
        type: "video",
        handler: (match) => match[1],
      },
      // Playlist URLs: ?list=playlistId
      {
        pattern: /[?&]list=([\w_-]+)/,
        type: "playlist",
        handler: (match) => match[1],
      },
    ]

    for (const { pattern, type, handler } of patterns) {
      const match = url.match(pattern)
      if (match) {
        const id = handler(match)

        if (type === "video") {
          // Return the proper embed URL format
          const embedBase = url.includes("-nocookie")
            ? "https://www.youtube-nocookie.com/embed"
            : "https://www.youtube.com/embed"
          return `${embedBase}/${id}`
        }

        if (type === "playlist") {
          const embedBase = url.includes("-nocookie")
            ? "https://www.youtube-nocookie.com/embed"
            : "https://www.youtube.com/embed"
          return `${embedBase}?list=${id}`
        }
      }
    }

    // If no patterns match, return null
    console.warn("Unable to convert YouTube URL:", url)
    return null
  } catch (error) {
    console.error("Invalid URL provided:", url, error)
    return null
  }
}

/**
 * Enhanced converter that also handles channel live streams
 * Note: This requires making a request to resolve the actual video ID
 */
export async function convertToEmbedUrlAdvanced(url) {
  // First try the basic conversion
  const basicResult = convertToEmbedUrl(url)
  if (basicResult) return basicResult

  // Handle channel live stream URLs that need special processing
  const channelLivePatterns = [
    /youtube\.com\/c\/([\w-]+)\/live/,
    /youtube\.com\/channel\/([\w-]+)\/live/,
    /youtube\.com\/@([\w-]+)\/live/,
  ]

  for (const pattern of channelLivePatterns) {
    const match = url.match(pattern)
    if (match) {
      const channelId = match[1]
      // For channel live streams, we can use YouTube's channel embed format
      return `https://www.youtube.com/embed/live_stream?channel=${channelId}`
    }
  }

  return null
}

/**
 * Utility to extract video ID from any YouTube URL
 */
export function extractVideoId(url) {
  const patterns = [
    /\/live\/([\w-]{8,15})/,
    /[?&]v=([\w-]{8,15})/,
    /youtu\.be\/([\w-]{8,15})/,
    /\/shorts\/([\w-]{8,15})/,
    /\/embed\/([\w-]{8,15})/,
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }

  return null
}
